import React, { useState, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import Header from './Header';
import Sidebar from './Sidebar';

const Layout = ({ activeModule, onModuleChange }) => {
  const { isMobile, isTablet, isDesktop } = useTheme();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeTab, setActiveTab] = useState('budget'); // Default to budget tab

  // Function to determine active tab based on current URL
  const getActiveTabFromUrl = (pathname) => {
    if (pathname.startsWith('/state/')) {
      return 'state';
    } else if (pathname.startsWith('/central/')) {
      return 'central';
    } else {
      return 'budget'; // Default for all other paths including dashboard, projects, session, budgetry
    }
  };

  // Auto-close sidebar on mobile when screen size changes
  useEffect(() => {
    if (isMobile) {
      setSidebarOpen(false);
    } else {
      // For desktop, you might want to open by default, or keep the last state
      // setSidebarOpen(true); // Uncomment if you want sidebar open by default on desktop
    }
  }, [isMobile]);

  // Set active tab based on current URL path
  useEffect(() => {
    const tabFromUrl = getActiveTabFromUrl(location.pathname);
    setActiveTab(tabFromUrl);
    
    // Also save to localStorage for consistency
    localStorage.setItem('activeTab', tabFromUrl);
  }, [location.pathname]);

  // Load active tab from localStorage on mount (but URL will override this immediately)
  useEffect(() => {
    const savedTab = localStorage.getItem('activeTab');
    if (savedTab && ['budget', 'state', 'central'].includes(savedTab)) {
      // Only use saved tab if it matches current URL context
      const urlTab = getActiveTabFromUrl(location.pathname);
      if (savedTab === urlTab) {
        setActiveTab(savedTab);
      }
    }
  }, [location.pathname]); // Added dependency to re-run when path changes

  const toggleSidebar = () => {
    const newState = !sidebarOpen;
    setSidebarOpen(newState);
    
    // Save sidebar state to localStorage (only for desktop/tablet)
    if (!isMobile) {
      localStorage.setItem('sidebarOpen', JSON.stringify(newState));
    }
  };
  
  const closeSidebar = () => {
    setSidebarOpen(false);
    // Save state to localStorage if on desktop
    if (!isMobile) {
      localStorage.setItem('sidebarOpen', 'false');
    }
  };

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    // Close sidebar on mobile when changing tabs
    if (isMobile) {
      closeSidebar();
    }
    // Save to localStorage
    localStorage.setItem('activeTab', tabId);
  };

  // Get sidebar width based on screen size and collapsed state
  const getSidebarWidth = () => {
    if (isMobile) {
      return 'w-80'; // Fixed width for mobile overlay
    }
    return sidebarOpen ? (isTablet ? 'w-64' : 'w-72') : 'w-0';
  };

  return (
    <div className="flex h-screen bg-bg-secondary">
      {/* Mobile Sidebar Overlay */}
      {isMobile && sidebarOpen && (
        <div 
          className="fixed inset-0 bg-bg-overlay z-40"
          onClick={closeSidebar}
        />
      )}

      {/* Sidebar */}
      <div className={`
        ${isMobile 
          ? `fixed left-0 top-0 h-full z-50 transform transition-transform duration-300 ${
              sidebarOpen ? 'translate-x-0' : '-translate-x-full'
            } w-80`
          : `relative transition-all duration-300 ${getSidebarWidth()} overflow-hidden`
        }
      `}>
        <div className={`
          h-full
          ${!isMobile && !sidebarOpen ? 'opacity-0' : 'opacity-100'}
          ${!isMobile ? 'transition-opacity duration-300' : ''}
          ${isMobile ? 'w-80' : isTablet ? 'w-64' : 'w-72'}
        `}>
          <Sidebar 
            activeModule={activeModule} 
            onModuleChange={(module) => {
              onModuleChange(module);
              if (isMobile) closeSidebar();
            }}
            isMobile={isMobile}
            isCollapsed={!isMobile && !sidebarOpen}
            activeTab={activeTab} // Pass active tab to sidebar
          />
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <Header 
          onToggleSidebar={toggleSidebar}
          showMenuButton={true}
          sidebarCollapsed={!sidebarOpen}
          activeTab={activeTab}
          onTabChange={handleTabChange}
        />
        <main className="flex-1 overflow-y-auto bg-bg-secondary">
          <div className="min-h-full">
            <div className={`
              ${isMobile ? 'p-4' : isTablet ? 'p-6' : 'p-8'}
            `}>
              <Outlet />
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;